import React from 'react';
import { Popover, TableBody, Table, TableCell, TableContainer, TableHead, TableRow, Paper } from '@mui/material';
import { formatLocalTime, toDateTime } from '../../../components/ex_dayjs';

interface IData {
    [key: string]: any;
}

const History: React.FC<IData> = (props) => {

    const { open, data, btnId } = props.info;
    const setHistoryPopup = props.setHistoryPopup;
    let btnPosition;
    if (btnId !== null)
        btnPosition = document.getElementById(btnId).getBoundingClientRect();

    const id = open ? 'simple-popover' : undefined;

    const handleClose = (_params) => {
        setHistoryPopup(prev => ({ ...prev, open: false, data: null }));
    };
    return (
        <Popover
            id={id}
            open={open}
            onClose={handleClose}
            anchorReference="anchorPosition"
            anchorPosition={{ top: btnPosition?.top + 45, left: btnPosition?.left }}
            anchorOrigin={{
                vertical: 'top',
                horizontal: 'center',
            }}
            transformOrigin={{
                vertical: 'top',
                horizontal: 'center',
            }}
        >
            <TableContainer component={Paper} sx={{
                pt: 1,
                borderRadius: 'sm',
                transition: '0.3s',
                background: (theme) =>
                    `linear-gradient(45deg, ${theme.vars.palette.primary[500]}, ${theme.vars.palette.primary[400]})`,
                '& tr:last-child': {
                    '& td:first-child': {
                        borderBottomLeftRadius: '8px',
                    },
                    '& td:last-child': {
                        borderBottomRightRadius: '8px',
                    },
                },
            }}>
                <Table sx={{ minWidth: 350 }} size="small" aria-label="a dense table">
                    <TableHead>
                        <TableRow>
                            <TableCell align="left">ExchangeId</TableCell>
                            <TableCell align="right">Qty</TableCell>
                            <TableCell align="right">Competitor price</TableCell>
                            <TableCell align="right">Updated date</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {data !== null && data.length > 0 && JSON.parse(data).sort((x: any, y: any) => toDateTime(y.Updated_on) - toDateTime(x.Updated_on)).map((row: any) => (
                            <TableRow
                                key={row.name}
                                sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                            >
                                <TableCell align="right">{row.ExchangeId}</TableCell>
                                <TableCell align="right">{row.Qty}</TableCell>
                                <TableCell align="right">{row.OriginalPrice}</TableCell>
                                <TableCell align="right">{row.Updated_on ? formatLocalTime(row.Updated_on) : ''}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Popover>
    );
};
export default React.memo(History);

/**
 * Popup component that displays the list price change history for an inventory item.
 *
 * @param {object} props
 * @param {{ open: boolean, data: string|null, btnId: string|null }} props.info - Popup state:
 *   `data` is the raw JSON string of price history entries (array of
 *   `{updated, value, userEmail, mobileApp, auditNote, inventoryId}`).
 * @param {Function} props.setListPriceHistoryPopup - State setter to close the popup.
 * @returns {JSX.Element}
 */
export const ListPriceHistory: React.FC<IData> = (props) => {
    const { open, data, btnId } = props.info;
    const setListPriceHistoryPopup = props.setListPriceHistoryPopup;

    let btnPosition;
    if (btnId !== null)
        btnPosition = document.getElementById(btnId)?.getBoundingClientRect();

    const id = open ? 'lp-history-popover' : undefined;

    const handleClose = () => {
        setListPriceHistoryPopup(prev => ({ ...prev, open: false, data: null }));
    };

    // Parse and sort entries by updated date descending
    const rows = data ? JSON.parse(data).sort((a: any, b: any) => new Date(b.updated).getTime() - new Date(a.updated).getTime()) : [];

    return (
        <Popover
            id={id}
            open={open}
            onClose={handleClose}
            anchorReference="anchorPosition"
            anchorPosition={{ top: (btnPosition?.top ?? 0) + 45, left: btnPosition?.left ?? 0 }}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
            <TableContainer component={Paper} sx={{
                pt: 1,
                borderRadius: 'sm',
                transition: '0.3s',
                background: (theme) =>
                    `linear-gradient(45deg, ${theme.vars.palette.primary[500]}, ${theme.vars.palette.primary[400]})`,
                '& tr:last-child': {
                    '& td:first-child': { borderBottomLeftRadius: '8px' },
                    '& td:last-child': { borderBottomRightRadius: '8px' },
                },
            }}>
                <Table sx={{ minWidth: 450 }} size="small" aria-label="list price history table">
                    <TableHead>
                        <TableRow>
                            <TableCell align="left">Updated</TableCell>
                            <TableCell align="right">Value</TableCell>
                            <TableCell align="left">User Email</TableCell>
                            <TableCell align="center">Mobile App</TableCell>
                            <TableCell align="left">Audit Note</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.map((row: any, idx: any) => (
                            <TableRow key={idx} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                <TableCell align="left">{row.updated ? formatLocalTime(row.updated) : ''}</TableCell>
                                <TableCell align="right">{row.value !== null ? parseFloat(row.value).toFixed(2) : ''}</TableCell>
                                <TableCell align="left">{row.userEmail ?? ''}</TableCell>
                                <TableCell align="center">{row.mobileApp ? 'Yes' : 'No'}</TableCell>
                                <TableCell align="left">{row.auditNote ?? ''}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Popover>
    );
};